import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useAuth } from "./AuthContext";
import { useToast } from "./ToastContext";
import { normalizeProductImages } from "../utils/imageUtils";
import { 
  clearUserCart,
  clearUserWishlist,
  subscribeToUserCart,
  subscribeToUserWishlist,
  addItemToCart,
  removeItemFromCart,
  updateCartItemQuantity,
  addItemToWishlist,
  removeItemFromWishlist,
  isItemInWishlist,
  getCartStats,
  getWishlistStats,
  getUserCart,
  getUserWishlist,
  getProductById
} from "../firebase/firestore";

const CartContext = createContext();

// Generate unique guest ID for this session
const getGuestUserId = () => {
  let guestId = sessionStorage.getItem('varaha_guest_id');
  if (!guestId) {
    guestId = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('varaha_guest_id', guestId);
  }
  return guestId;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { currentUser, loading: authLoading } = useAuth();
  const { showToast } = useToast();
  const [cartItems, setCartItems] = useState([]);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true); // Start as true to prevent flash of empty state
  const [error, setError] = useState(null);
  // Use ref to access current cartItems in callbacks without stale closures
  const cartItemsRef = useRef([]);
  
  // Keep ref in sync with state
  useEffect(() => {
    cartItemsRef.current = cartItems;
  }, [cartItems]);

  // Load cart and wishlist from Firestore when auth is ready
  // NON-BLOCKING: Sets up listeners immediately, loads data in background
  useEffect(() => {
    if (authLoading) {
      return;
    }

    let cartUnsubscribe = null;
    let wishlistUnsubscribe = null;
    let cancelled = false;

    const setupUserData = async () => {
      const userId = currentUser ? currentUser.uid : getGuestUserId();

      // Set loading state but don't block UI
      setIsLoading(true);
      setError(null);
      
      // Set up real-time listeners FIRST (non-blocking)
      // These will provide data from cache immediately if available
      try {
        cartUnsubscribe = subscribeToUserCart(userId, (cartData) => {
        if (!cancelled) {
          // Normalize cart items to ensure proper image structure
          // Also enrich with product data if base64Data is missing
          const enrichCartItems = async (items) => {
            // Get current cart items to preserve base64Data from optimistic updates
            // Use ref to avoid stale closure
            const currentItems = cartItemsRef.current;
            const currentItemsMap = new Map(currentItems.map(item => [item.id, item]));
            
            const enrichedItems = await Promise.all(items.map(async (item) => {
              // CRITICAL: If current optimistic state has base64Data, preserve it
              const currentItem = currentItemsMap.get(item.id);
              if (currentItem?.base64Data) {
                // Preserve base64Data from optimistic update
                item.base64Data = currentItem.base64Data;
              }
              
              // If item doesn't have base64Data or imageUrl, try to fetch from product
              // This ensures images are visible even for items added before the base64Data fix
              if ((!item.base64Data && !item.imageUrl) && item.id) {
                try {
                  const fullProduct = await getProductById(item.id);
                  if (fullProduct) {
                    // Merge the base64Data from the full product (highest priority)
                    if (fullProduct.base64Data) {
                      return { ...item, base64Data: fullProduct.base64Data };
                    }
                    // Fallback to imageUrl if base64Data is not available
                    if (fullProduct.imageUrl && !fullProduct.imageUrl.startsWith('blob:')) {
                      return { ...item, imageUrl: fullProduct.imageUrl };
                    }
                  }
                } catch (error) {
                  console.warn('Could not fetch product image for cart item:', item.id);
                }
              }
              return item;
            }));
            // DON'T normalize here - preserve base64Data, getProductImage() will handle it
            return enrichedItems;
          };
          
          enrichCartItems(cartData).then(enrichedCartData => {
            if (!cancelled) {
              setCartItems(enrichedCartData);
              setIsLoading(false);
            }
          });
        }
        });

        wishlistUnsubscribe = subscribeToUserWishlist(userId, (wishlistData) => {
          if (!cancelled) {
            // Enrich wishlist items with images if missing (same as cart)
            const enrichWishlistItems = async (items) => {
              const enrichedItems = await Promise.all(items.map(async (item) => {
                // If item doesn't have base64Data or imageUrl, try to fetch from product
                if ((!item.base64Data && !item.imageUrl) && item.id) {
                  try {
                    const fullProduct = await getProductById(item.id);
                    if (fullProduct) {
                      if (fullProduct.base64Data) {
                        return { ...item, base64Data: fullProduct.base64Data };
                      }
                      if (fullProduct.imageUrl && !fullProduct.imageUrl.startsWith('blob:')) {
                        return { ...item, imageUrl: fullProduct.imageUrl };
                      }
                    }
                  } catch (error) {
                    console.warn('Could not fetch product image for wishlist item:', item.id);
                  }
                }
                return item;
              }));
              // DON'T normalize here - preserve base64Data, getProductImage() will handle it
              return enrichedItems;
            };
            
            enrichWishlistItems(wishlistData).then(enrichedWishlistData => {
              if (!cancelled) {
                setWishlistItems(enrichedWishlistData);
              }
            });
          }
        });
      } catch (listenerError) {
        console.error('❌ Error setting up Firebase listeners:', listenerError);
        if (!cancelled) {
          setError(listenerError.message);
        }
      }

      // Load initial data in background (non-blocking)
      // Safe helpers will use cache if offline, so this won't block
      try {
        const [cartData, wishlistData] = await Promise.all([
          getUserCart(userId), // Safe helper - never throws
          getUserWishlist(userId) // Safe helper - never throws
        ]);

        if (!cancelled) {
          // OPTIMIZED: Set items immediately for new users (non-blocking)
          // This prevents blocking the UI while enrichment happens
          setCartItems(cartData);
          setWishlistItems(wishlistData);
          setIsLoading(false);
          
          // Enrich items in background if needed (non-blocking for new users)
          // Only enrich if there are items to enrich
          if (cartData.length > 0 || wishlistData.length > 0) {
            const enrichCartItems = async (items) => {
              const enrichedItems = await Promise.all(items.map(async (item) => {
                // If item doesn't have base64Data or imageUrl, try to fetch from product
                if ((!item.base64Data && !item.imageUrl) && item.id) {
                  try {
                    const fullProduct = await getProductById(item.id);
                    if (fullProduct) {
                      // Merge the base64Data from the full product (highest priority)
                      if (fullProduct.base64Data) {
                        return { ...item, base64Data: fullProduct.base64Data };
                      }
                      // Fallback to imageUrl if base64Data is not available
                      if (fullProduct.imageUrl && !fullProduct.imageUrl.startsWith('blob:')) {
                        return { ...item, imageUrl: fullProduct.imageUrl };
                      }
                    }
                  } catch (error) {
                    console.warn('Could not fetch product image for cart item:', item.id);
                  }
                }
                return item;
              }));
              // DON'T normalize - preserve base64Data
              return enrichedItems;
            };
            
            // Enrich wishlist items with product images if missing
            const enrichWishlistItems = async (items) => {
              const enrichedItems = await Promise.all(items.map(async (item) => {
                // If item doesn't have base64Data or imageUrl, try to fetch from product
                if ((!item.base64Data && !item.imageUrl) && item.id) {
                  try {
                    const fullProduct = await getProductById(item.id);
                    if (fullProduct) {
                      if (fullProduct.base64Data) {
                        return { ...item, base64Data: fullProduct.base64Data };
                      }
                      if (fullProduct.imageUrl && !fullProduct.imageUrl.startsWith('blob:')) {
                        return { ...item, imageUrl: fullProduct.imageUrl };
                      }
                    }
                  } catch (error) {
                    console.warn('Could not fetch product image for wishlist item:', item.id);
                  }
                }
                return item;
              }));
              // DON'T normalize - preserve base64Data
              return enrichedItems;
            };
            
            // Enrich in background (non-blocking)
            Promise.all([
              cartData.length > 0 ? enrichCartItems(cartData) : Promise.resolve([]),
              wishlistData.length > 0 ? enrichWishlistItems(wishlistData) : Promise.resolve([])
            ]).then(([enrichedCart, enrichedWishlist]) => {
              if (!cancelled) {
                // Only update if we got enriched data
                if (enrichedCart.length > 0) {
                  setCartItems(enrichedCart);
                }
                if (enrichedWishlist.length > 0) {
                  setWishlistItems(enrichedWishlist);
                }
              }
            }).catch(error => {
              // Silently fail - items are already set, enrichment is just a bonus
              console.warn('Background enrichment failed:', error);
            });
          }
        }
      } catch (error) {
        // This should never happen since safe helpers don't throw,
        // but just in case:
        if (!cancelled) {
          console.error('❌ Error loading initial cart/wishlist:', error);
          setError(error.message);
          setCartItems([]);
          setWishlistItems([]);
          setIsLoading(false);
        }
      }
    };

    setupUserData();

    // Cleanup listeners when component unmounts or user changes
    return () => {
      cancelled = true;
      if (cartUnsubscribe) {
        cartUnsubscribe();
      }
      if (wishlistUnsubscribe) {
        wishlistUnsubscribe();
      }
    };
  }, [currentUser, authLoading]);

  // Note: We don't need auto-save useEffect hooks here because:
  // 1. Direct functions (addItemToCart, removeItemFromCart, etc.) already save to Firestore
  // 2. Real-time listeners keep state in sync with Firestore
  // 3. Auto-save would create a write loop: state change → save → listener update → state change → save...

  const addToCart = async (product) => {
    
    // Normalize the product to ensure proper image structure
    let normalizedProduct = normalizeProductImages(product);
    
    // CRITICAL: If product doesn't have base64Data after normalization, try to fetch it
    // This ensures we always have image data even if the product object is incomplete
    if (!normalizedProduct.base64Data && normalizedProduct.id) {
      try {
        const fullProduct = await getProductById(normalizedProduct.id);
        if (fullProduct?.base64Data) {
          normalizedProduct.base64Data = fullProduct.base64Data;
        }
        // Also check selectedVariant - if we have a selected variant, try to get its image
        if (normalizedProduct.selectedVariant && !normalizedProduct.selectedVariant.base64Data) {
          // Try to find the variant in the full product's colorVariants
          if (fullProduct?.colorVariants && Array.isArray(fullProduct.colorVariants)) {
            const matchingVariant = fullProduct.colorVariants.find(
              v => v.id === normalizedProduct.selectedVariant.id || 
                   v.color === normalizedProduct.selectedVariant.color
            );
            if (matchingVariant?.base64Data) {
              normalizedProduct.selectedVariant.base64Data = matchingVariant.base64Data;
              // Also set it on the main product if still missing
              if (!normalizedProduct.base64Data) {
                normalizedProduct.base64Data = matchingVariant.base64Data;
              }
            }
          }
        }
      } catch (error) {
        console.warn('Could not fetch product image data:', error);
      }
    }
    
    const productName = normalizedProduct.name || 'Item';
    const quantityToAdd = normalizedProduct.quantity || 1;
    
    // Check stock availability before adding to cart
    const productStock = normalizedProduct.stock;
    let stockAdjusted = false;
    
    // If product has stock property, validate it
    if (productStock !== undefined && productStock !== null) {
      const currentCartItem = cartItems.find(item => item.id === normalizedProduct.id);
      const currentCartQuantity = currentCartItem ? currentCartItem.quantity : 0;
      const newTotalQuantity = currentCartQuantity + quantityToAdd;
      
      // Check if stock is available
      if (productStock <= 0) {
        showToast(`${productName} is out of stock!`, 'error');
        return; // Don't throw error, just return early after showing toast
      }
      
      // Check if adding this quantity would exceed available stock
      if (newTotalQuantity > productStock) {
        const availableToAdd = productStock - currentCartQuantity;
        if (availableToAdd <= 0) {
          showToast(`Only ${productStock} ${productStock === 1 ? 'item' : 'items'} available in stock. Already in cart!`, 'error');
          return; // Don't throw error, just return early after showing toast
        } else {
          // Adjust quantity to available stock
          normalizedProduct.quantity = availableToAdd;
          stockAdjusted = true;
          showToast(`Only ${availableToAdd} more ${availableToAdd === 1 ? 'item' : 'items'} available. Added ${availableToAdd} to cart.`, 'warning');
        }
      }
    }
    
    // Show notification immediately for instant feedback (only if not already shown)
    if (!stockAdjusted) {
      showToast(`${productName} added to cart!`, 'success');
    }
    
    // Optimistically update local state immediately
    // CRITICAL: Preserve base64Data from the original product for immediate display
    // This ensures images are visible on checkout immediately after add-to-cart
    const productWithNormalizedImages = normalizeProductImages(normalizedProduct);
    
    // Ensure base64Data is preserved even after normalization (normalizeProductImages preserves it, but double-check)
    // CRITICAL: base64Data is the source of truth for getProductImage to create blob URLs
    if (normalizedProduct.base64Data && !productWithNormalizedImages.base64Data) {
      productWithNormalizedImages.base64Data = normalizedProduct.base64Data;
    }
    // Also check selectedVariant.base64Data if main product doesn't have it
    if (!productWithNormalizedImages.base64Data && normalizedProduct.selectedVariant?.base64Data) {
      productWithNormalizedImages.base64Data = normalizedProduct.selectedVariant.base64Data;
    }
    // Also ensure imageUrl is set if we have base64Data (for fallback, though getProductImage uses base64Data first)
    // But don't overwrite existing imageUrl if it's a valid persistent URL
    if (productWithNormalizedImages.base64Data && (!productWithNormalizedImages.imageUrl || productWithNormalizedImages.imageUrl.startsWith('blob:'))) {
      // imageUrl will be created by getProductImage from base64Data, so we don't need to set it here
      // But we ensure base64Data is present so getProductImage can work
    }
    
    // DEBUG: Log to verify base64Data is preserved in optimistic update
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 addToCart - Optimistic Update:', {
        productId: normalizedProduct.id,
        productName: normalizedProduct.name,
        hasBase64Data: !!productWithNormalizedImages.base64Data,
        base64DataLength: productWithNormalizedImages.base64Data?.length,
        selectedVariantHasBase64: !!normalizedProduct.selectedVariant?.base64Data,
        originalBase64Data: !!normalizedProduct.base64Data
      });
    }
    
    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => item.id === normalizedProduct.id);
      if (existingItem) {
        const finalQuantity = existingItem.quantity + (normalizedProduct.quantity || 1);
        // Double-check stock limit in state update
        if (productStock !== undefined && productStock !== null && finalQuantity > productStock) {
          return prevItems.map(item =>
            item.id === normalizedProduct.id
              ? { ...normalizeProductImages(item), quantity: productStock }
              : item
          );
        }
        // Merge with existing item, ensuring base64Data is preserved
        // CRITICAL: Preserve base64Data so checkout can display images immediately
        const mergedItem = { ...existingItem, ...productWithNormalizedImages, quantity: finalQuantity };
        // Ensure base64Data is preserved from normalizedProduct (highest priority)
        if (normalizedProduct.base64Data && !mergedItem.base64Data) {
          mergedItem.base64Data = normalizedProduct.base64Data;
        }
        // Also check selectedVariant.base64Data
        if (!mergedItem.base64Data && normalizedProduct.selectedVariant?.base64Data) {
          mergedItem.base64Data = normalizedProduct.selectedVariant.base64Data;
        }
        // Preserve existing base64Data if new one is missing (don't lose it during merge)
        if (!mergedItem.base64Data && existingItem.base64Data) {
          mergedItem.base64Data = existingItem.base64Data;
        }
        // Normalize but preserve base64Data (normalizeProductImages should preserve it, but ensure it)
        const normalizedMerged = normalizeProductImages(mergedItem);
        if (mergedItem.base64Data && !normalizedMerged.base64Data) {
          normalizedMerged.base64Data = mergedItem.base64Data;
        }
        return prevItems.map(item =>
          item.id === normalizedProduct.id
            ? normalizedMerged
            : item
        );
      } else {
        // Add new item with normalized images so they display immediately
        // CRITICAL: Ensure base64Data is explicitly preserved so checkout can display images immediately
        const newItem = { ...productWithNormalizedImages, quantity: normalizedProduct.quantity || 1 };
        // Double-check base64Data is preserved (this is the source of truth for getProductImage)
        if (normalizedProduct.base64Data && !newItem.base64Data) {
          newItem.base64Data = normalizedProduct.base64Data;
        }
        // Also check selectedVariant.base64Data
        if (!newItem.base64Data && normalizedProduct.selectedVariant?.base64Data) {
          newItem.base64Data = normalizedProduct.selectedVariant.base64Data;
        }
        // Ensure base64Data is present - this is critical for immediate image display
        if (!newItem.base64Data) {
          console.warn('⚠️ Cart item missing base64Data - image may not display immediately:', {
            productId: normalizedProduct.id,
            productName: normalizedProduct.name,
            hasImageUrl: !!newItem.imageUrl,
            imageUrl: newItem.imageUrl
          });
        }
        return [...prevItems, newItem];
      }
    });
    
    // Then sync with Firebase in the background
    try {
      const userId = currentUser ? currentUser.uid : getGuestUserId();
      const updatedCartItems = await addItemToCart(userId, normalizedProduct, normalizedProduct.quantity || 1);
      // Normalize cart items immediately to ensure images are available right away
      // This prevents the need for a page refresh to see images
      // CRITICAL: Preserve base64Data from optimistic update when merging with server state
      const currentOptimisticItems = cartItemsRef.current;
      const currentItemsMap = new Map(currentOptimisticItems.map(item => [item.id, item]));
      const normalizedCartItems = updatedCartItems.map(item => {
        const normalized = normalizeProductImages(item);
        // Preserve base64Data from optimistic update if server state doesn't have it
        const optimisticItem = currentItemsMap.get(item.id);
        if (optimisticItem?.base64Data && !normalized.base64Data) {
          normalized.base64Data = optimisticItem.base64Data;
        }
        return normalized;
      });
      // Update with server state to ensure consistency, but preserve base64Data from optimistic updates
      setCartItems(normalizedCartItems);
      return normalizedCartItems; // Return normalized cart items for await support
    } catch (error) {
      console.error('❌ Error adding to cart:', error);
      // If error is about stock, show appropriate message
      if (error.message && error.message.includes('stock')) {
        showToast(error.message, 'error');
        // Revert optimistic update by reloading cart
        const userId = currentUser ? currentUser.uid : getGuestUserId();
        const currentCart = await getUserCart(userId).catch(() => []);
        setCartItems(currentCart.map(item => normalizeProductImages(item)));
      }
      // Re-throw error so callers can handle it
      throw error;
    }
  };

  // Buy Now function - adds to cart and returns promise for navigation
  const buyNow = async (product) => {
    try {
      await addToCart(product);
      return true; // Success
    } catch (error) {
      console.error('❌ Error in buyNow:', error);
      // Return false on error, but don't throw - let caller decide navigation
      return false;
    }
  };

  const removeFromCart = async (productId) => {
    
    try {
      const userId = currentUser ? currentUser.uid : getGuestUserId();
      const updatedCartItems = await removeItemFromCart(userId, productId);
      setCartItems(updatedCartItems);
      // Product removed from cart successfully
    } catch (error) {
      console.error('❌ Error removing from cart:', error);
      // Fallback to local state update
      setCartItems(prevItems => prevItems.filter(item => item.id !== productId));
    }
  };

  const updateCartQuantity = async (productId, quantity) => {
    
    if (quantity <= 0) {
      await removeFromCart(productId);
    } else {
      // Check stock before updating quantity
      const cartItem = cartItems.find(item => item.id === productId);
      if (cartItem && cartItem.stock !== undefined && cartItem.stock !== null) {
        if (quantity > cartItem.stock) {
          showToast(`Only ${cartItem.stock} ${cartItem.stock === 1 ? 'item' : 'items'} available in stock!`, 'error');
          // Set quantity to max available stock
          quantity = cartItem.stock;
        }
      }
      
      try {
        const userId = currentUser ? currentUser.uid : getGuestUserId();
        const updatedCartItems = await updateCartItemQuantity(userId, productId, quantity);
        setCartItems(updatedCartItems);
        // Cart quantity updated successfully
      } catch (error) {
        console.error('❌ Error updating cart quantity:', error);
        // If error is about stock, show appropriate message
        if (error.message && error.message.includes('stock')) {
          showToast(error.message, 'error');
          // Revert to current cart state
          const userId = currentUser ? currentUser.uid : getGuestUserId();
          const currentCart = await getUserCart(userId).catch(() => []);
          setCartItems(currentCart.map(item => normalizeProductImages(item)));
        } else {
          // Fallback to local state update
          setCartItems(prevItems =>
            prevItems.map(item =>
              item.id === productId ? { ...item, quantity } : item
            )
          );
        }
      }
    }
  };

  const addToWishlist = async (product) => {
    // Normalize the product to ensure proper image structure (same as addToCart)
    let normalizedProduct = normalizeProductImages(product);
    
    // CRITICAL: If product doesn't have base64Data after normalization, try to fetch it
    // This ensures we always have image data even if the product object is incomplete
    if (!normalizedProduct.base64Data && normalizedProduct.id) {
      try {
        const fullProduct = await getProductById(normalizedProduct.id);
        if (fullProduct?.base64Data) {
          normalizedProduct.base64Data = fullProduct.base64Data;
        }
        // Also check selectedVariant if available
        if (normalizedProduct.selectedVariant && !normalizedProduct.selectedVariant.base64Data) {
          if (fullProduct?.colorVariants && Array.isArray(fullProduct.colorVariants)) {
            const matchingVariant = fullProduct.colorVariants.find(
              v => v.id === normalizedProduct.selectedVariant.id || 
                   v.color === normalizedProduct.selectedVariant.color
            );
            if (matchingVariant?.base64Data) {
              normalizedProduct.selectedVariant.base64Data = matchingVariant.base64Data;
              // Also set it on the main product if still missing
              if (!normalizedProduct.base64Data) {
                normalizedProduct.base64Data = matchingVariant.base64Data;
              }
            }
          }
        }
      } catch (error) {
        console.warn('Could not fetch product image data for wishlist:', error);
      }
    }
    
    try {
      const userId = currentUser ? currentUser.uid : getGuestUserId();
      const updatedWishlistItems = await addItemToWishlist(userId, normalizedProduct);
      setWishlistItems(updatedWishlistItems);
      // Product added to wishlist successfully
    } catch (error) {
      console.error('❌ Error adding to wishlist:', error);
      // Fallback to local state update
      setWishlistItems(prevItems => {
        const existingItem = prevItems.find(item => item.id === normalizedProduct.id);
        if (!existingItem) {
          return [...prevItems, normalizedProduct];
        }
        return prevItems;
      });
    }
  };

  const removeFromWishlist = async (productId) => {
    
    try {
      const userId = currentUser ? currentUser.uid : getGuestUserId();
      const updatedWishlistItems = await removeItemFromWishlist(userId, productId);
      setWishlistItems(updatedWishlistItems);
      // Product removed from wishlist successfully
    } catch (error) {
      console.error('❌ Error removing from wishlist:', error);
      // Fallback to local state update
      setWishlistItems(prevItems => prevItems.filter(item => item.id !== productId));
    }
  };

  const isInWishlist = async (productId) => {
    
    try {
      const userId = currentUser ? currentUser.uid : getGuestUserId();
      const inWishlist = await isItemInWishlist(userId, productId);
      return inWishlist;
    } catch (error) {
      console.error('❌ Error checking wishlist status:', error);
      // Fallback to local state check
      return wishlistItems.some(item => item.id === productId);
    }
  };

  const toggleWishlist = async (product) => {
    // Check local state first to avoid race conditions
    const inWishlistLocal = wishlistItems.some(item => item.id === product.id);
    
    if (inWishlistLocal) {
      // Item is in local wishlist, remove it
      await removeFromWishlist(product.id);
    } else {
      // Item is not in local wishlist, add it
      await addToWishlist(product);
    }
  };

  const getCartCount = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const getWishlistCount = () => {
    return wishlistItems.length;
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getWishlistTotal = () => {
    return wishlistItems.reduce((total, item) => total + item.price, 0);
  };

  const clearCart = async () => {
    
    try {
      const userId = currentUser ? currentUser.uid : getGuestUserId();
      await clearUserCart(userId);
      setCartItems([]);
      // Cart cleared successfully
    } catch (error) {
      console.error('❌ Error clearing cart:', error);
      // Fallback to local state clear
      setCartItems([]);
    }
  };

  const clearWishlist = async () => {
    
    try {
      const userId = currentUser ? currentUser.uid : getGuestUserId();
      await clearUserWishlist(userId);
      setWishlistItems([]);
      // Wishlist cleared successfully
    } catch (error) {
      console.error('❌ Error clearing wishlist:', error);
      // Fallback to local state clear
      setWishlistItems([]);
    }
  };

  // Get cart and wishlist statistics
  const getCartStatistics = async () => {
    
    try {
      const userId = currentUser ? currentUser.uid : getGuestUserId();
      const stats = await getCartStats(userId);
      return stats;
    } catch (error) {
      console.error('❌ Error getting cart statistics:', error);
      // Fallback to local state calculation
      return { totalItems: getCartCount(), totalValue: getCartTotal(), itemCount: cartItems.length };
    }
  };

  const getWishlistStatistics = async () => {
    
    try {
      const userId = currentUser ? currentUser.uid : getGuestUserId();
      const stats = await getWishlistStats(userId);
      return stats;
    } catch (error) {
      console.error('❌ Error getting wishlist statistics:', error);
      // Fallback to local state calculation
      return { totalItems: getWishlistCount(), totalValue: getWishlistTotal() };
    }
  };

  const value = {
    cartItems,
    wishlistItems,
    isLoading,
    error,
    addToCart,
    buyNow,
    removeFromCart,
    updateCartQuantity,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    toggleWishlist,
    getCartCount,
    getWishlistCount,
    getCartTotal,
    getWishlistTotal,
    clearCart,
    clearWishlist,
    getCartStatistics,
    getWishlistStatistics
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};


